import axios from "axios";

const apiBaseUrl = String(
  process.env.REACT_APP_API_URL || ""
).replace(/\/+$/, "");

const apiUrl = `${apiBaseUrl}/api/tutorials`;

/* -------------------------------------------------------------------------- */
/*                              Configuration                                 */
/* -------------------------------------------------------------------------- */

/*
 * Le token est récupéré à chaque requête.
 * Cela évite de conserver un ancien token après une connexion/déconnexion.
 */
const getAuthConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
  };
};

const logServiceError = (message, error) => {
  console.error(message, error.response?.data || error.message || error);
};

/* -------------------------------------------------------------------------- */
/*                           TUTORIEL PUBLIC                                   */
/* -------------------------------------------------------------------------- */

/**
 * Récupère le tutoriel publié.
 *
 * Exemple :
 *
 * getPublicTutorial({
 *   appKey: "monappli-club",
 *   siteKey: "usbouscat-judo",
 *   role: "utilisateur",
 * });
 */
export const getPublicTutorial = async ({
  appKey = "monappli-club",
  siteKey,
  locale = "fr-FR",
  role,
  version,
} = {}) => {
  try {
    const response = await axios.get(`${apiUrl}/public`, {
      params: {
        app_key: appKey,
        site_key: siteKey || undefined,
        locale,
        role: role || undefined,
        version: version || undefined,
      },
    });

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la récupération du tutoriel public :",
      error
    );

    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                              TUTORIELS ADMIN                               */
/* -------------------------------------------------------------------------- */

/**
 * Récupère tous les tutoriels.
 */
export const getAllTutorials = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/admin`,
      getAuthConfig()
    );

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    logServiceError(
      "Erreur lors de la récupération des tutoriels :",
      error
    );

    return [];
  }
};

/**
 * Récupère un tutoriel complet par son identifiant.
 */
export const getTutorialById = async (tutorialId) => {
  try {
    const response = await axios.get(
      `${apiUrl}/admin/${tutorialId}`,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la récupération du tutoriel :",
      error
    );

    return null;
  }
};

/**
 * Crée un tutoriel.
 */
export const addTutorial = async (tutorialData) => {
  try {
    const response = await axios.post(
      `${apiUrl}/admin`,
      tutorialData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la création du tutoriel :",
      error
    );

    return null;
  }
};

/**
 * Modifie les informations générales d’un tutoriel.
 */
export const updateTutorial = async (
  tutorialId,
  tutorialData
) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/admin/${tutorialId}`,
      tutorialData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la modification du tutoriel :",
      error
    );

    return null;
  }
};

/**
 * Supprime un tutoriel et tout son contenu.
 */
export const deleteTutorial = async (tutorialId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/admin/${tutorialId}`,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la suppression du tutoriel :",
      error
    );

    return null;
  }
};

/**
 * Publie une version du tutoriel.
 *
 * L’ancienne version publiée sera automatiquement archivée.
 */
export const publishTutorial = async (tutorialId) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/admin/${tutorialId}/publish`,
      {},
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la publication du tutoriel :",
      error
    );

    return null;
  }
};

/**
 * Duplique un tutoriel.
 *
 * duplicateData peut contenir :
 * {
 *   version: 2,
 *   titre: "Tutoriel version 2",
 *   app_key: "monappli-club",
 *   site_key: null,
 *   locale: "fr-FR"
 * }
 */
export const duplicateTutorial = async (
  tutorialId,
  duplicateData = {}
) => {
  try {
    const response = await axios.post(
      `${apiUrl}/admin/${tutorialId}/duplicate`,
      duplicateData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la duplication du tutoriel :",
      error
    );

    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                                  SECTIONS                                  */
/* -------------------------------------------------------------------------- */

/**
 * Ajoute une section à un tutoriel.
 */
export const addTutorialSection = async (
  tutorialId,
  sectionData
) => {
  try {
    const response = await axios.post(
      `${apiUrl}/admin/${tutorialId}/sections`,
      sectionData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la création de la section :",
      error
    );

    return null;
  }
};

/**
 * Modifie une section.
 */
export const updateTutorialSection = async (
  sectionId,
  sectionData
) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/admin/sections/${sectionId}`,
      sectionData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la modification de la section :",
      error
    );

    return null;
  }
};

/**
 * Supprime une section et ses étapes.
 */
export const deleteTutorialSection = async (sectionId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/admin/sections/${sectionId}`,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la suppression de la section :",
      error
    );

    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                                   ÉTAPES                                   */
/* -------------------------------------------------------------------------- */

/**
 * Ajoute une étape à une section.
 */
export const addTutorialStep = async (
  sectionId,
  stepData
) => {
  try {
    const response = await axios.post(
      `${apiUrl}/admin/sections/${sectionId}/steps`,
      stepData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la création de l’étape :",
      error
    );

    return null;
  }
};

/**
 * Modifie une étape.
 */
export const updateTutorialStep = async (
  stepId,
  stepData
) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/admin/steps/${stepId}`,
      stepData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la modification de l’étape :",
      error
    );

    return null;
  }
};

/**
 * Supprime une étape et ses médias.
 */
export const deleteTutorialStep = async (stepId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/admin/steps/${stepId}`,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la suppression de l’étape :",
      error
    );

    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                                   MÉDIAS                                   */
/* -------------------------------------------------------------------------- */

/**
 * Ajoute un média existant à une étape.
 *
 * Exemple :
 * {
 *   type_media: "image",
 *   url: "/img/Tutoriel/connexion_01.png",
 *   alt_text: "Créer son compte",
 *   legende: "Touchez pour agrandir",
 *   ordre: 10,
 *   is_active: true
 * }
 */
export const addTutorialMedia = async (
  stepId,
  mediaData
) => {
  try {
    const response = await axios.post(
      `${apiUrl}/admin/steps/${stepId}/medias`,
      mediaData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la création du média :",
      error
    );

    return null;
  }
};

/**
 * Modifie un média.
 */
export const updateTutorialMedia = async (
  mediaId,
  mediaData
) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/admin/medias/${mediaId}`,
      mediaData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la modification du média :",
      error
    );

    return null;
  }
};

/**
 * Supprime un média.
 */
export const deleteTutorialMedia = async (mediaId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/admin/medias/${mediaId}`,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la suppression du média :",
      error
    );

    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                                    FAQ                                     */
/* -------------------------------------------------------------------------- */

/**
 * Ajoute une question fréquente.
 */
export const addTutorialFaq = async (
  tutorialId,
  faqData
) => {
  try {
    const response = await axios.post(
      `${apiUrl}/admin/${tutorialId}/faqs`,
      faqData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la création de la FAQ :",
      error
    );

    return null;
  }
};

/**
 * Modifie une question fréquente.
 */
export const updateTutorialFaq = async (
  faqId,
  faqData
) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/admin/faqs/${faqId}`,
      faqData,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la modification de la FAQ :",
      error
    );

    return null;
  }
};

/**
 * Supprime une question fréquente.
 */
export const deleteTutorialFaq = async (faqId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/admin/faqs/${faqId}`,
      getAuthConfig()
    );

    return response.data;
  } catch (error) {
    logServiceError(
      "Erreur lors de la suppression de la FAQ :",
      error
    );

    return null;
  }
};